/**
 * useDeleteRodeo — Singular wrapper around the rodeo-delete workflow endpoint
 * (`DELETE /jackpot-ops/rodeo/:id`) that invalidates BOTH the base generated
 * CRUD keys and the extension/workflow keys used by delegate/rodeo pages.
 *
 * Why the workflow endpoint (not the generated `RodeosClient.delete`):
 *   - The generated CRUD delete does a raw `DELETE FROM rodeos`, which trips
 *     the rodeo's NO ACTION foreign keys (rounds, daysheets, buddy_groups,
 *     event children) — the "fails to delete on an FK constraint" bug.
 *   - The workflow endpoint enforces the deletability rule (no signups, no
 *     initialized draw, not published → 409 otherwise) and deletes the FK
 *     subtree in the correct order so the delete actually succeeds.
 *
 * Why a singular wrapper:
 *   - The generated hook is plural (`useDeleteRodeos`) and only invalidates
 *     the base `rodeos` cache — it does NOT know about the workflow cache
 *     (`rodeo-ops`, `jackpot-ops`) that pages like `DelegateRodeosPage` and
 *     `RodeoDetailPage` read from.
 *   - This hook takes ownership of that broader invalidation so consumers
 *     don't have to remember which caches depend on a rodeo delete.
 *
 * Caches invalidated on success:
 *   - `rodeosKeys.all`    — base CRUD list/detail
 *   - `rodeoOpsKeys.all`  — DelegateRodeosPage, RodeoDetailPage, useMyRodeos
 *   - `jackpotOpsKeys.all` — shared `/jackpot-ops/list` list (rodeos + jackpots)
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { httpClient } from '../api/http-client';
import { rodeosKeys } from '../api/generated_hooks/generated-rodeos-hooks';
import { rodeoOpsKeys } from '../api/extensions/useRodeoOps';
import { jackpotOpsKeys } from '../api/extensions/useJackpotOps';

/**
 * Delete a rodeo by id and invalidate every cache that could contain it.
 *
 * @returns React Query mutation: `mutate(id)` / `mutateAsync(id)`.
 */
export function useDeleteRodeo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      httpClient.delete<{ message: string }>(`/jackpot-ops/rodeo/${id}`),
    onSuccess: () => {
      // Base generated CRUD cache
      queryClient.invalidateQueries({ queryKey: rodeosKeys.all });
      // Extension/workflow caches that render rodeos to the user
      queryClient.invalidateQueries({ queryKey: rodeoOpsKeys.all });
      // The /jackpot-ops/list endpoint backs BOTH rodeo and jackpot lists;
      // a rodeo delete can change its output.
      queryClient.invalidateQueries({ queryKey: jackpotOpsKeys.all });
    },
  });
}
